import { Fragment, useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { List, ListItem, ListItemIcon, ListItemText, Collapse } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from 'components';
import routes from 'config/routes';
import { useReduxSelector } from 'store/hooks';

const SidebarList = (props) => {
  const theme = useTheme();

  const location = useLocation();
  const app = useReduxSelector((state) => state.app);
  const account = useReduxSelector((state) => state.account);
  const [open, setOpen] = useState(() =>
    routes.filter((item) => item.sub).reduce((prev, item) => ({ ...prev, [item.id]: false }), {})
  );
  const classes = makeStyles((theme) => ({
    navLink: {
      textDecoration: 'none',
    },
    navItem: {
      paddingLeft: 10,
    },
    activeNavItem: {
      backgroundColor: theme.palette.primary.contrastText,
      borderRadius: '50px 0px 0px 50px',
      height: 30,
    },
    openNavItem: {
      marginLeft: 14,
    },
    decorationContainer: {
      backgroundColor: theme.palette.primary.contrastText,
      height: 10,
    },
    decoration: {
      backgroundColor: app.env.colors?.primary || theme.palette.primary.main,
      height: '100%',
    },
    subNavItem: {
      padding: '5px 0px 5px 50px',
    },
    navIcon: {
      height: 24,
      width: 24,
      marginRight: 10,
      color: theme.palette.primary.contrastText,
    },
    activeNavIcon: {
      color: app.env.colors?.primary || theme.palette.secondary.main,
    },
    navItemText: {
      padding: 0,
    },
    faIcon: {
      marginLeft: 2,
      marginRight: 2,
    },
    expandIcon: {
      marginRight: 30,
    },
    activeSubItem: {
      width: 5,
      height: 5,
      backgroundColor: theme.palette.primary.contrastText,
      borderRadius: '50%',
      marginRight: 10,
    },
  }))();

  const activeRoute = (path, subPath) => {
    let isActive = false;
    if (path) {
      isActive = location.pathname.indexOf(path) === 0;
      if (subPath) {
        isActive = location.pathname.substring(path.length).indexOf(subPath) === 0;
      }
    }
    return isActive;
  };

  const handleExpandToggle = (id) => () => {
    setOpen({ ...open, [id]: !open[id] });
  };

  let enabledRoutes = routes;

  if (account.data?.inChargeDivision && account.data.inChargeDivision?.includes('Mahasiswa')) {
    enabledRoutes = routes.filter((route) =>
      ['user', 'shift', 'dashboard', 'transaction', 'devices'].includes(route.id)
    );
  }

  if (!account.data.isHR) {
    if (account.data.isSPV || (account.data.access && Array.isArray(account.data.access))) {
      enabledRoutes = routes.filter(
        (item) => account.data.access?.includes(item.id) || item?.access?.includes('SPV')
      );
    } else {
      enabledRoutes = routes.filter(({ hr }) => !hr);
    }
  }

  if (account.data?.isDirectors) {
    enabledRoutes = routes.filter((route) => ['dashboard'].includes(route.id));
  }

  return (
    <List>
      {enabledRoutes.map((item) => {
        if (!item.navbar) return null;

        const navItemClass = clsx(
          {
            [classes.activeNavItem]: activeRoute(item.path),
            [classes.openNavItem]: props.open,
          },
          classes.navItem
        );
        const navIconClass = clsx(
          {
            [classes.activeNavIcon]: activeRoute(item.path),
          },
          classes.navIcon
        );

        if (!item.sub) {
          return (
            <NavLink to={item.path} key={item.id} className={classes.navLink}>
              {activeRoute(item.path) && (
                <div className={classes.decorationContainer}>
                  <div
                    className={classes.decoration}
                    style={{ borderRadius: '0px 0px 50px 0px' }}
                  />
                </div>
              )}
              <ListItem button={!activeRoute(item.path)} disableGutters className={navItemClass}>
                <ListItemIcon className={navIconClass}>
                  <FontAwesomeIcon icon={item.icon} size={'1x'} className={classes.faIcon} />
                </ListItemIcon>
                <ListItemText
                  className={classes.navItemText}
                  primary={
                    <Typography
                      variant="body1"
                      color={
                        activeRoute(item.path)
                          ? app.env.colors?.primary || theme.palette.secondary.main
                          : theme.palette.primary.contrastText
                      }
                      type={activeRoute(item.path) && 'bold'}
                    >
                      {item.title}
                    </Typography>
                  }
                />
              </ListItem>
              {activeRoute(item.path) && (
                <div className={classes.decorationContainer}>
                  <div
                    className={classes.decoration}
                    style={{ borderRadius: '0px 50px 0px 0px' }}
                  />
                </div>
              )}
            </NavLink>
          );
        } else {
          return (
            <Fragment key={item.id}>
              {activeRoute(item.path) && (
                <div className={classes.decorationContainer}>
                  <div
                    className={classes.decoration}
                    style={{ borderRadius: '0px 0px 50px 0px' }}
                  />
                </div>
              )}
              <ListItem
                button={!activeRoute(item.path)}
                disableGutters
                className={navItemClass}
                onClick={handleExpandToggle(item.id)}
              >
                <ListItemIcon className={navIconClass}>
                  <FontAwesomeIcon icon={item.icon} className={classes.faIcon} />
                </ListItemIcon>
                <ListItemText
                  className={classes.navItemText}
                  primary={
                    <Typography
                      variant="body1"
                      color={
                        activeRoute(item.path)
                          ? app.env.colors?.primary || theme.palette.secondary.main
                          : theme.palette.primary.contrastText
                      }
                      type={activeRoute(item.path) && 'bold'}
                    >
                      {item.title}
                    </Typography>
                  }
                />
                {open[item.id] ? (
                  <FontAwesomeIcon
                    icon="chevron-up"
                    className={classes.expandIcon}
                    color={
                      activeRoute(item.path)
                        ? app.env.colors?.primary || theme.palette.secondary.main
                        : theme.palette.primary.contrastText
                    }
                  />
                ) : (
                  <FontAwesomeIcon
                    icon="chevron-down"
                    className={classes.expandIcon}
                    color={
                      activeRoute(item.path)
                        ? app.env.colors?.primary || theme.palette.secondary.main
                        : theme.palette.primary.contrastText
                    }
                  />
                )}
              </ListItem>
              {activeRoute(item.path) && (
                <div className={classes.decorationContainer}>
                  <div
                    className={classes.decoration}
                    style={{ borderRadius: '0px 50px 0px 0px' }}
                  />
                </div>
              )}
              <Collapse in={props.open && open[item.id]} timeout="auto" unmountOnExit>
                {item.sub.map((subitem) => (
                  <NavLink
                    to={item.path + subitem.path}
                    key={subitem.id}
                    className={classes.navLink}
                  >
                    <ListItem button disableGutters className={classes.subNavItem}>
                      {activeRoute(item.path, subitem.path) && (
                        <ListItemIcon className={classes.activeSubItem} />
                      )}
                      <ListItemText
                        className={classes.navItemText}
                        primary={
                          <Typography variant="body1" color={theme.palette.primary.contrastText}>
                            {subitem.title}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </NavLink>
                ))}
              </Collapse>
            </Fragment>
          );
        }
      })}
    </List>
  );
};

export default SidebarList;
